import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Save, RotateCcw, Mail, MessageSquare, Bell } from "lucide-react";

const TEMPLATE_META = {
    pickup_reminder: { label: "Pickup day reminder", icon: Bell, note: "Sent the night before every pickup day to that route's customers." },
    payment_reminder: { label: "Payment past due", icon: Mail, note: "Sent when an invoice becomes overdue." },
    welcome: { label: "New customer welcome", icon: Mail, note: "Sent right after a customer signs up via the website widget." },
    holiday_schedule: { label: "Holiday schedule shift", icon: Bell, note: "Sent when a holiday shifts the route schedule." },
    weather_delay: { label: "Weather delay", icon: MessageSquare, note: "Manual — send when severe weather forces a route delay." },
};

export default function TemplatesPage() {
    const [templates, setTemplates] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [draft, setDraft] = useState({ subject: "", body: "" });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const { data } = await api.get("/communications/templates");
        setTemplates(data);
        if (data.length && !activeId) setActiveId(data[0].id);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    useEffect(() => {
        const cur = templates.find((t) => t.id === activeId);
        if (cur) setDraft({ subject: cur.subject || "", body: cur.body || "" });
    }, [activeId, templates]);

    const active = templates.find((t) => t.id === activeId);
    const meta = TEMPLATE_META[activeId] || { label: activeId, icon: Mail, note: "" };
    const dirty = active && (draft.subject !== active.subject || draft.body !== active.body);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/communications/templates/${activeId}`, draft);
            toast.success("Template saved");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const reset = async () => {
        if (!window.confirm("Reset this template to the built-in default?")) return;
        try {
            await api.post(`/communications/templates/${activeId}/reset`);
            toast.success("Reset to default");
            await load();
        } catch (e) {
            toast.error("Reset failed");
        }
    };

    const preview = (draft.body || "").replace(/{{customer_name}}/g, "Tyler");

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <PageHeader
                title="Email & SMS templates"
                subtitle="Edit the wording customers see. Use {{customer_name}} to inject their first name."
            />
            <div className="grid grid-cols-12 gap-4">
                <div className="col-span-4 space-y-1.5">
                    {templates.map((t) => {
                        const m = TEMPLATE_META[t.id] || { label: t.id, icon: Mail };
                        const Icon = m.icon;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setActiveId(t.id)}
                                data-testid={`template-tab-${t.id}`}
                                className={
                                    "w-full text-left px-3 py-2.5 rounded-md border flex items-start gap-2 transition " +
                                    (activeId === t.id
                                        ? "border-primary bg-primary/5"
                                        : "border-border hover:bg-secondary/40")
                                }
                            >
                                <Icon className={"w-4 h-4 mt-0.5 shrink-0 " + (activeId === t.id ? "text-primary" : "text-muted-foreground")} />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium truncate">{m.label}</div>
                                    <div className="text-[10px] text-muted-foreground truncate">{t.subject}</div>
                                    {t.is_customized && (
                                        <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">Customized</div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="col-span-8">
                    {active && (
                        <Card>
                            <CardContent className="p-5 space-y-4">
                                <div>
                                    <div className="text-xs text-muted-foreground">{meta.note}</div>
                                </div>
                                <div>
                                    <Label className="text-xs mb-1 block">Subject line</Label>
                                    <Input
                                        value={draft.subject}
                                        onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
                                        data-testid="template-subject-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs mb-1 block">Message body</Label>
                                    <textarea
                                        value={draft.body}
                                        onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                                        rows={10}
                                        className="w-full font-mono text-sm rounded-md border border-input bg-transparent px-3 py-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                        data-testid="template-body-input"
                                    />
                                    <div className="text-[10px] text-muted-foreground mt-1">
                                        Line breaks become paragraphs in the email. Same body is used for SMS if the template is broadcast to phone.
                                    </div>
                                </div>
                                <div className="rounded-md border border-border p-3 bg-secondary/30">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                                        Preview (as it would send to Tyler)
                                    </div>
                                    <div className="text-sm font-semibold">{draft.subject}</div>
                                    <div className="text-sm whitespace-pre-wrap mt-1">{preview}</div>
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <Button variant="ghost" onClick={reset} data-testid="template-reset-btn">
                                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                        Reset to default
                                    </Button>
                                    <Button onClick={save} disabled={!dirty || saving} data-testid="template-save-btn">
                                        <Save className="w-3.5 h-3.5 mr-1" />
                                        {saving ? "Saving…" : "Save"}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
